import Utils, {deepCopy} from "../utils/utils";
import {
    AccessLevelPackages,
    AccessLevelPackagesWithAggregates,
    BizFormPackage,
    BizInventoryPackage,
    BizPointPackage,
    BizPrintPackage,
    ClientsPlatformPackage,
    DashboardPackage,
    LookupPackage,
    NCRPackage,
    NotificationsPackage,
    PurchasingPackage,
    SalesPackage,
    SupportPackage,
    WarrantyAndReturnPackage
} from "./types";
import EnvService from "../env";
import {initialPackages} from "./pre-defined";

/**
 * Access levels service responsible for managing the access levels of the application.
 */
class AccessLevelsService {
    private static _packages: AccessLevelPackages = deepCopy(initialPackages);

    /**
     * The access level packages of this service
     */
    public static get packages(): AccessLevelPackagesWithAggregates {
        return Object.fromEntries(
            Object
                .entries(this._packages)
                .map(([key, value]) => [key, {
                    ...value,
                    accessToAll: Object.values(value).every((v) => v),
                    accessToAny: Object.values(value).some((v) => v),
                    get accessToMenuItems() {
                        switch (key as keyof AccessLevelPackages) {
                            case "lookup": {
                                const pack = value as LookupPackage;
                                return pack.nexusSearch || pack.comparisonList
                                    || pack.viewAllCompetitorPriceReports
                                    || pack.viewMyCompetitorPriceReports;
                            }
                            case "bizInventory": {
                                const pack = value as BizInventoryPackage;
                                return pack.transferHistoryList ||
                                    pack.adjustmentList ||
                                    pack.adjustmentItems ||
                                    pack.adjustmentReports ||
                                    pack.viewAvailabilityThresholds;
                            }
                            case "warrantyAndReturn": {
                                const pack = value as WarrantyAndReturnPackage;
                                return pack.viewList
                                    || pack.editConfigurations
                                    || pack.drivers;
                            }
                            case "ncr": {
                                const pack = value as NCRPackage;
                                return pack.viewList ||
                                    pack.notes ||
                                    pack.tasks ||
                                    pack.editConfigurations;
                            }
                            case "bizPrint": {
                                const pack = value as BizPrintPackage;
                                return pack.printCatalogue
                                    || pack.printHub
                                    || pack.printPackingList
                                    || pack.printLabels
                            }
                            case "clientPlatform": {
                                const pack = value as ClientsPlatformPackage;
                                return pack.webHomepageEditions ||
                                    pack.phoneHomepageEditions ||
                                    pack.contentManagement ||
                                    pack.surveys ||
                                    pack.themeManagement ||
                                    pack.bannerManagement ||
                                    pack.viewDynamicPages;
                            }
                            case "sales": {
                                const pack = value as SalesPackage;
                                return pack.quotesList ||
                                    pack.salesOrdersList ||
                                    pack.invoicesList ||
                                    pack.creditsList ||
                                    pack.promotions ||
                                    pack.salesPerson;
                            }
                            case "purchasing": {
                                const pack = value as PurchasingPackage;
                                return pack.viewPurchaseOrders ||
                                    pack.viewPurchaseHistory ||
                                    pack.viewPurchasePlanning;
                            }
                            case "notifications": {
                                const pack = value as NotificationsPackage;
                                return pack.customerNotifications ||
                                    pack.manageCustomerNotifications;
                            }
                            case "dashboard" : {
                                const pack = value as DashboardPackage;
                                return pack.systemWideDashboards ||
                                    pack.systemWideReports ||
                                    pack.lookupAnalytics ||
                                    pack.lostSalesAnalytics ||
                                    pack.viewDashboards ||
                                    pack.lookupLogs ||
                                    pack.lostSalesLogs;
                            }
                            case "bizForm": {
                                const pack = value as BizFormPackage;
                                return pack.formsList || pack.formResponsesList;
                            }
                            case "bizPoint": {
                                const pack = value as BizPointPackage;
                                return pack.loyaltyDashboard
                                    || pack.loyaltyBuyBiz
                                    || pack.loyaltyEarningRules
                                    || pack.loyaltyCustomers
                                    || pack.loyaltySettings
                                    || pack.adminReviewQueue
                                    || pack.adminManualAdjustment
                                    || pack.adminBusinessConfig
                                    || pack.adminLiabilityReport
                                    || pack.adminAuditLog
                                    || pack.adminPurchaseConfirm
                                    || pack.adminWalletSuspend;
                            }
                            case "support": {
                                const pack = value as SupportPackage;
                                return pack.contactUsMessages;
                            }
                            default:
                                return false;
                        }
                    },
                    accessToMenuItemsWithParams(...args: any[]) {
                        switch (key as keyof AccessLevelPackages) {
                            case "purchasing": {
                                const purchasingKeyForPlanning = args[0];
                                const pack = value as PurchasingPackage;
                                return pack.viewPurchaseOrders ||
                                    pack.viewPurchaseHistory ||
                                    (pack.viewPurchasePlanning && !!purchasingKeyForPlanning);
                            }
                            default:
                                return false;
                        }
                    },
                }])
        ) as AccessLevelPackagesWithAggregates;
    }

    /**
     * Set the packages of this service.
     *
     * This method will merge the new packages with the existing packages. so you can set only the packages you want to change.
     * @param packages
     */
    static setPackages(packages: DeepPartial<AccessLevelPackages>): void {
        this._packages = Utils.deepMerge(
            {},
            this._packages,
            packages ?? {},
            this.getPackageOverrides(),
        ) as AccessLevelPackages;
    }


    /**
     * Get the package overrides for the given partner id.
     */
    private static getPackageOverrides(): DeepPartial<AccessLevelPackages> {
        return Utils.deepMerge(
            {},
            {
                ...(!EnvService.DEV_showPurchase ? {
                    purchasing: {
                        viewPurchaseOrders: false,
                        exportPurchaseOrdersList: false,
                        submitPurchaseOrder: false,
                        viewPurchaseOrderDetails: false,
                        managePurchaseOrderDetails: false,
                        viewPurchaseHistory: false,
                        exportPurchaseHistory: false,
                    },
                } as DeepPartial<AccessLevelPackages> : {})
            },
            {
                ...(!EnvService.DEV_showFastConnection ? {
                    lookup: {
                        fastConnection: false,
                    },
                } as DeepPartial<AccessLevelPackages> : {})
            },
            {
                ...(!EnvService.DEV_showVendors ? {
                    base: {
                        viewVendors: false,
                        exportVendorsList: false,
                        createNewVendor: false,
                        viewVendorAccountInfo: false,
                        manageVendorAccountInfo: false,
                        viewVendorNotes: false,
                        exportVendorNotes: false,
                        manageVendorNotes: false,
                        viewVendorProducts: false,
                        manageVendorProducts: false,
                        exportVendorProducts: false,
                    },
                } as DeepPartial<AccessLevelPackages> : {})
            },
            {
                ...(!EnvService.DEV_showCompetitorPriceReports ? {
                    lookup: {
                        viewCompetitors: false,
                        manageCompetitors: false,
                        exportCompetitors: false,
                        reportCompetitorPrice: false,
                        viewAllCompetitorPriceReports: false,
                        viewMyCompetitorPriceReports: false,
                        manageCompetitorPriceReports: false,
                        exportCompetitorPriceReports: false,
                    },
                } as DeepPartial<AccessLevelPackages> : {})
            },
            {
                ...(!EnvService.DEV_showDamageControl ? {
                    sales: {
                        troubleshootTransactionsList: false,
                    },
                    lookup: {
                        missingPublicProductsList: false,
                    },
                } as DeepPartial<AccessLevelPackages> : {})
            },
            {
                ...(!EnvService.DEV_showInventoryTransfer ? {
                    bizInventory: {
                        minimumRequiredAvailability: false,
                        manageMinimumRequiredAvailability: false,
                        transferList: false,
                        submitTransfer: false,
                        transferHistoryList: false,
                        transferHistoryItems: false,
                        transferInformation: false,
                    },
                } as DeepPartial<AccessLevelPackages> : {}),
            },
            {
                ...(!EnvService.DEV_showSetInventoryAvailability ? {
                    bizInventory: {
                        setInventoryAvailability: false,
                    },
                } as DeepPartial<AccessLevelPackages> : {}),
            },
            {
                ...(!EnvService.DEV_showCMSFeature ? {
                    base: {
                        contentBlogs: false,
                        contentVideos: false,
                    },
                } as DeepPartial<AccessLevelPackages> : {})
            },
            {
                ...(!EnvService.DEV_showCustomerLoginLogs ? {
                    base: {
                        customerLoginLogs: false,
                    },
                } as DeepPartial<AccessLevelPackages> : {})
            },
            {
                ...(!EnvService.DEV_showBizPrintEmailLink ? {
                    bizPrint: {
                        emailTemplate: false,
                    },
                } as DeepPartial<AccessLevelPackages> : {})
            },
            {
                ...(!EnvService.DEV_showVinDecoder ? {
                    lookup: {
                        vinDecoder: false,
                    },
                } as DeepPartial<AccessLevelPackages> : {})
            },
            {
                ...(!EnvService.DEV_multiLanguageSupport ? {
                    base: {
                        spanishLanguageSupport: false,
                        frenchLanguageSupport: false,
                    }
                } as DeepPartial<AccessLevelPackages> : {})
            },
            {
                ...(!EnvService.DEV_showProductOnBackorders ? {
                    lookup: {
                        productOnBackorderList: false,
                    }
                } as DeepPartial<AccessLevelPackages> : {})
            },
            {
                ...(!EnvService.DEV_showFormManagement ? {
                    bizForm: {
                        formsList: false,
                        formsManage: false,
                        formResponsesList: false,
                        formResponsesManage: false,
                    },
                } as DeepPartial<AccessLevelPackages> : {}),
            },
            {
                ...(!EnvService.DEV_showBizPoint ? {
                    bizPoint: {
                        loyaltyDashboard: false,
                        loyaltyBuyBiz: false,
                        loyaltyEarningRules: false,
                        loyaltyEarningRulesManage: false,
                        loyaltyCustomers: false,
                        loyaltyCustomersManage: false,
                        loyaltySettings: false,
                        adminReviewQueue: false,
                        adminManualAdjustment: false,
                        adminBusinessConfig: false,
                        adminLiabilityReport: false,
                        adminAuditLog: false,
                        adminPurchaseConfirm: false,
                        adminWalletSuspend: false,
                    },
                } as DeepPartial<AccessLevelPackages> : {}),
            },
            {
                ...(!EnvService.DEV_showDynamicPages ? {
                    clientPlatform: {
                        viewDynamicPages: false,
                        manageDynamicPages: false,
                    },
                } as DeepPartial<AccessLevelPackages> : {}),
            },
            {
                ...(!EnvService.DEV_showSupport ? {
                    support: {
                        contactUsMessages: false,
                    },
                } as DeepPartial<AccessLevelPackages> : {}),
            },
            {
                ...(!EnvService.DEV_freightEstimation ? {
                    sales: {
                        regionRates: false,
                        manageRegionRates: false,
                        partRates: false,
                        managePartRates: false,
                    },
                    base: {
                        freightProviderSettings: false,
                    },
                } as DeepPartial<AccessLevelPackages> : {}),
            },
        )
    }
}


export * from "./types";
export default AccessLevelsService;
